import ApiClientAsync from "../../api/ApiClientAsync";
import decisionModal from "../decisionModal";
import { toFrontendBackupData } from "../../redux/modules/tvchartData/dto/toFrontendBackupData";
import normalizeChartAnalysisDetails from "./normalizeChartAnalysisDetails";

/**
 * Build injected handlers for createChartActionsBridge from view props/routines.
 */
export default function buildTvChartBridgeChartActionHandlers({
  stockId,
  getChartTemplatesState,
  getChartAnalysesState,
  getBackups,
  addChartTemplateAsync,
  updateChartTemplateAsync,
  removeChartTemplateAsync,
  fetchChartTemplateDetailsAsync,
  fetchChartAnalysesAsync,
  addChartAnalysisAsync,
  updateChartAnalysisAsync,
  setChartAnalysisVisibilityAsync,
  removeChartAnalysisAsync,
  fetchChartAnalysisDetailsAsync
}) {
  const withStockId = payload => ({ ...(payload || {}), stockId });

  const getTemplatesList = () => {
    const state = getChartTemplatesState?.() || {};
    return {
      list: state.list || [],
      defaultTemplate: state.defaultTemplate || null
    };
  };

  const getAnalysesList = async () => {
    if (typeof fetchChartAnalysesAsync === "function") {
      await fetchChartAnalysesAsync({ stockId });
    }

    const state = getChartAnalysesState?.() || {};
    return {
      list: state.list || []
    };
  };

  const getBackupsList = () => getBackups?.() || [];

  const fetchChartTemplateDetailsById = async id => {
    const { list } = getTemplatesList();
    const template = (list || []).find(item => item.id === id);
    if (!template?.url) {
      throw new Error("Template not found");
    }

    let settings = await fetchChartTemplateDetailsAsync(template.url);

    if (settings?.layout && settings?.charts) {
      settings = { chart: settings };
    }

    return {
      chart: settings.chart,
      drawnObjectsOptions: settings.drawnObjectsOptions
    };
  };

  const fetchAnalysisDetailsById = async id => {
    const state = getChartAnalysesState?.() || {};
    const analysis = (state.list || []).find(item => item.id === id);
    if (!analysis?.url) {
      throw new Error("Analysis not found");
    }

    const raw = await fetchChartAnalysisDetailsAsync(analysis.url);
    return normalizeChartAnalysisDetails(raw);
  };

  const fetchBackupDetails = async url => {
    const apiClient = new ApiClientAsync();
    const data = await apiClient.get({ url });
    const backupData = toFrontendBackupData(data);

    if (typeof backupData === "undefined") {
      throw new Error("Failed backup data transformation");
    }

    return {
      chart:
        typeof backupData.chart === "string"
          ? JSON.parse(backupData.chart)
          : backupData.chart,
      settings: backupData.settings
    };
  };

  const confirm = async ({
    title,
    message,
    confirmLabel,
    secondaryConfirmLabel,
    decisions
  } = {}) => {
    const nextDecisions =
      decisions ||
      (secondaryConfirmLabel
        ? ["confirm", "cancel", "secondaryConfirm"]
        : ["confirm", "cancel"]);

    const decisionLabels = {};
    if (confirmLabel) {
      decisionLabels.confirm = confirmLabel;
    }
    if (secondaryConfirmLabel) {
      decisionLabels.secondaryConfirm = secondaryConfirmLabel;
    }

    return decisionModal(
      "ConfirmModal",
      {
        title,
        message
      },
      nextDecisions,
      decisionLabels
    );
  };

  return {
    getTemplatesList,
    getAnalysesList,
    getBackupsList,
    addChartTemplate: addChartTemplateAsync,
    updateChartTemplate: updateChartTemplateAsync,
    removeChartTemplate: removeChartTemplateAsync,
    fetchChartTemplateDetailsById,
    addChartAnalysis: payload => addChartAnalysisAsync?.(withStockId(payload)),
    updateChartAnalysisMeta: payload =>
      updateChartAnalysisAsync?.(withStockId(payload)),
    setChartAnalysisVisibility: payload =>
      setChartAnalysisVisibilityAsync?.(withStockId(payload)),
    removeChartAnalysis: payload =>
      removeChartAnalysisAsync?.(withStockId(payload)),
    fetchAnalysisDetailsById,
    fetchBackupDetails,
    confirm
  };
}
